/**
 * Il modulo con cui si avvia **o** si pianifica una discovery.
 *
 * Uno solo, con due esiti. Le due azioni chiedono le stesse identiche cose —
 * ambito, credenziale, opzioni — e differiscono per un blocco: quando eseguire.
 * Tenerne due separati significherebbe due elenchi di opzioni che divergono al
 * primo connettore aggiunto, e chi ha appena avviato una scansione a mano
 * dovrebbe reinserire tutto per ripeterla ogni notte.
 */
import { useEffect, useMemo, useState } from 'react'
import type { FormEvent, ReactNode } from 'react'

import { Link } from 'react-router-dom'

import { api } from '../api/client'
import type { CredentialGroup } from '../api/types'
import { ErrorBanner } from './common'
import type { Credential, DiscoveryKind, ScheduleDraft } from '../api/types'
import { useAsync } from '../hooks/useAsync'
import { localeCorrente, t } from '../i18n/lingua'

export type Mode = 'run' | 'schedule'

/**
 * Quale controllo rende ciascuna opzione dichiarata dal catalogo.
 *
 * Non è documentazione: è la tabella su cui `tests/test_discovery_catalog.py`
 * verifica che ogni opzione a catalogo abbia davvero un campo in questa pagina,
 * e che ogni campo corrisponda a un'opzione che qualcuno dichiara.
 *
 * Senza, il controllo poteva solo cercare il **nome** dell'opzione nel sorgente
 * — e il nome compare anche nell'invio del modulo, quindi togliere il campo dallo
 * schermo lasciandone l'invio non veniva rilevato: il tipo di discovery
 * risultava configurabile e non lo era.
 */
export const OPTION_FIELDS: Record<string, string> = {
  plan: 'df-plan',
  port: 'df-port',
  concurrency: 'df-conc',
  deep: 'df-deep',
  ping_first: 'df-ping',
  reverse_dns: 'df-dns',
  topology: 'df-topo',
  record_unmanaged: 'df-unmanaged',
  skip_covered: 'df-skip-covered',
  mac_table: 'df-mactable',
  entity_inventory: 'df-entity',
  routing_table: 'df-routing',
  sample_root: 'df-sample-root',
}

const CRON_PRESETS: { value: string; label: () => string }[] = [
  { value: '0 2 * * *', label: () => t('Ogni notte alle 2:00') },
  { value: '0 3 * * 0', label: () => t('Ogni domenica alle 3:00') },
  { value: '0 */6 * * *', label: () => t('Ogni 6 ore') },
  { value: '0 1 1 * *', label: () => t('Il primo del mese all’1:00') },
]

/**
 * Una frase tradotta con un collegamento in mezzo, al posto di `{link}`.
 *
 * La frase si traduce intera e si spezza dopo: tradurre i due pezzi a parte
 * obbligherebbe ogni lingua all'ordine delle parole dell'italiano.
 */
function conLink(frase: string, link: ReactNode): ReactNode {
  const [prima, dopo] = frase.split('{link}')
  return (
    <>
      {prima}
      {link}
      {dopo}
    </>
  )
}

export default function DiscoveryForm({
  kind,
  credentials,
  credentialGroups = [],
  mode,
  initial,
  onDone,
  onCancel,
}: {
  kind: DiscoveryKind
  credentials: Credential[]
  credentialGroups?: CredentialGroup[]
  mode: Mode
  /**
   * Una scansione già fatta, da cui il modulo parte **già compilato**.
   *
   * Chi ha lanciato quella scansione a mano ha già fatto le prove: ambito,
   * credenziale, sonda e opzioni hanno funzionato. Ricomporli a memoria in un
   * modulo vuoto è il punto in cui se ne sbaglia uno, e una scansione
   * pianificata con l'ambito quasi giusto non fallisce — produce un inventario
   * plausibile e incompleto.
   *
   * Il modulo resta modificabile: è una bozza, non una copia.
   */
  initial?: ScheduleDraft
  onDone: (message: string) => void
  onCancel: () => void
}) {
  const [targets, setTargets] = useState(initial ? initial.targets.join(', ') : '')
  const [credentialId, setCredentialId] = useState(
    initial?.credential_group_id
      ? `group:${initial.credential_group_id}`
      : (initial?.credential_id ?? ''),
  )
  const [port, setPort] = useState(initial?.port ? String(initial.port) : '')
  const [concurrency, setConcurrency] = useState(
    initial?.concurrency ? String(initial.concurrency) : '',
  )
  const [deep, setDeep] = useState(initial?.deep ?? true)
  // Da una bozza si prende il valore **della scansione**, non il default del
  // modulo: sono due domande diverse — «cosa conviene di solito» e «cos'è stato
  // fatto quella volta» — e rispondere alla prima quando è stata posta la
  // seconda pianificherebbe una scansione diversa da quella che ha funzionato.
  const [pingFirst, setPingFirst] = useState(initial ? initial.ping_first : true)
  const [reverseDns, setReverseDns] = useState(initial?.reverse_dns ?? false)
  const [recordUnmanaged, setRecordUnmanaged] = useState(initial?.record_unmanaged ?? false)
  const [entityInventory, setEntityInventory] = useState(initial?.entity_inventory ?? false)
  const [routingTable, setRoutingTable] = useState(initial?.routing_table ?? false)
  const [sampleRoot, setSampleRoot] = useState(initial?.sample_root ?? '')
  const [topology, setTopology] = useState(initial?.topology ?? false)
  const [skipCovered, setSkipCovered] = useState(initial?.skip_covered ?? false)
  const [macTable, setMacTable] = useState(initial?.mac_table ?? false)
  // Acceso di default: NETCONF ha un delimitatore, quindi non si indovina dove
  // finisce la risposta e non c'è paginazione da spegnere. Dove non è
  // abilitato sull'apparato si ripiega sulla CLI da sé.
  // Spento di norma: una configurazione senza credenziali non si può
  // ripristinare, e chi la scarica dopo un guasto se ne accorge nel momento
  // peggiore. Un backup che non ripristina non è un backup.
  // Su quale sonda. Vuoto = «qualunque sonda idonea», che resta il default: nella
  // maggior parte delle installazioni c'è una sonda sola, e obbligare a sceglierla
  // sarebbe un passo in più per non decidere niente.
  const [runOn, setRunOn] = useState(
    initial?.probe_id
      ? `probe:${initial.probe_id}`
      : initial?.group_id
        ? `group:${initial.group_id}`
        : '',
  )
  const [planId, setPlanId] = useState(initial?.plan_id ?? '')
  // I pool scelti. Un pool **è** un ambito: chi ne sceglie uno non deve
  // reincollare le stesse subnet accanto, che è precisamente il difetto per
  // cui i pool esistono.
  const [poolIds, setPoolIds] = useState<string[]>(initial?.pool_ids ?? [])
  const pools = useAsync(() => (kind.needs_targets ? api.pools() : Promise.resolve([])), [
    kind.kind,
  ])
  // Di quale ambito si sta parlando. Una scelta e non due campi che si sommano:
  // la somma silenziosa era la parte fuorviante, perché «cosa succede se li
  // riempio entrambi?» non aveva una risposta visibile da nessuna parte.
  //
  // Si parte dal pool solo se la scansione da cui si ricompila ne aveva uno:
  // altrimenti il campo di testo, che è quello che si usa la prima volta —
  // quando i pool non esistono ancora.
  const [scopeMode, setScopeMode] = useState<'manual' | 'pool'>(
    initial?.pool_ids?.length ? 'pool' : 'manual',
  )
  const scelti = (pools.data ?? []).filter((p) => poolIds.includes(p.id))
  const indirizziDeiPool = scelti.reduce((somma, p) => somma + p.addresses, 0)
  // Il tetto lo dichiara il Control Plane, uguale per tutti i pool: si legge da
  // uno qualunque invece di riscriverlo qui, o il giorno che cambia la console
  // continuerebbe a dire il numero vecchio.
  const tettoIndirizzi = pools.data?.[0]?.max_addresses ?? 65536
  // I tipi che si dividono da sé (rete, SNMP) non hanno un tetto sul totale:
  // oltre il limite della sonda per job, la scansione diventa più job.
  const tettoParte = pools.data?.[0]?.job_limits?.[kind.kind]
  const parti = tettoParte ? Math.ceil(indirizziDeiPool / tettoParte) : 1
  const oltreIlTetto = !tettoParte && indirizziDeiPool > tettoIndirizzi
  const probes = useAsync(() => api.probes(), [])
  const groups = useAsync(() => api.groups(), [])
  // Nell'«Esegui su» si mostrano solo le sonde che sanno eseguire *questo*
  // tipo: una senza la capability richiesta non è una scelta, è un job che
  // resterebbe in attesa senza spiegazione. Si filtra sulle capability
  // **effettive** (dichiarate ∩ abilitate), le stesse su cui l'orchestrator
  // assegna davvero (`eligible_probes`). Lo stato online **non** entra qui: una
  // sonda spenta con la capability giusta è una scelta legittima — il job la
  // aspetta (è il senso di fissare la sonda) — e resta in elenco marcata «non
  // in linea». Un gruppo compare finché ha almeno una sonda idonea.
  const sondeIdonee = useMemo(
    () => (probes.data ?? []).filter((p) => p.effective_capabilities.includes(kind.capability)),
    [probes.data, kind.capability],
  )
  const gruppiIdonei = useMemo(() => {
    const conSonda = new Set(
      sondeIdonee.map((p) => p.group_id).filter((id): id is string => id != null),
    )
    return (groups.data ?? []).filter((g) => conSonda.has(g.id))
  }, [groups.data, sondeIdonee])
  // I piani si chiedono solo ai tipi che li usano: caricarli sempre farebbe una
  // richiesta a ogni apertura del modulo per un elenco che nessuno guarderà.
  const plans = useAsync(
    () => (kind.options.includes('plan') ? api.discoveryPlans() : Promise.resolve([])),
    [kind.kind],
  )

  // Ricorrenza (solo in modalità pianificazione).
  const [name, setName] = useState(initial?.name ?? '')
  // Titolo e descrizione del job lanciato adesso: con decine di job al giorno,
  // tipo e ambito non dicono perché è stato lanciato né per chi.
  const [titolo, setTitolo] = useState('')
  const [descrizione, setDescrizione] = useState('')
  const [cron, setCron] = useState('0 2 * * *')
  const [timezone, setTimezone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
  )

  // Il backup configurazioni insieme alla scansione: un flag, non un secondo
  // modulo. Alla conferma parte (o si pianifica) un secondo job config_backup
  // sugli stessi bersagli, con la credenziale SSH scelta qui.
  const [backupConfig, setBackupConfig] = useState(false)
  const [backupCredentialId, setBackupCredentialId] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  // Solo le credenziali del tipo giusto: una community SNMP su un job AWS
  // verrebbe rifiutata, e capire perché richiederebbe di leggere i log.
  const usable = credentials.filter((c) => kind.credential_kinds.includes(c.kind))

  // Un gruppo è proponibile se **almeno una** delle sue credenziali è del tipo
  // giusto: la sonda prova in sequenza e salta quelle che non si applicano, quindi
  // un gruppo misto resta utile. Richiedere che lo siano tutte escluderebbe il caso
  // più comune — un gruppo «SNMP» con dentro v2c e v3.
  const usableGroups = credentialGroups.filter((g) =>
    g.members.some((m: CredentialGroup['members'][number]) => m.kind && kind.credential_kinds.includes(m.kind)),
  )

  // Cambiando tipo, una credenziale non più pertinente resterebbe selezionata
  // in silenzio e la discovery fallirebbe alla creazione.
  const allowed = kind.credential_kinds.join(',')
  useEffect(() => {
    setCredentialId((current) => {
      const chosen = credentials.find((c) => c.id === current)
      return chosen && !allowed.includes(chosen.kind) ? '' : current
    })
  }, [allowed, credentials])

  // La sonda o il gruppo fissati tornano a «qualunque» se il tipo cambia e non
  // sanno più eseguirlo: un `value` che non è fra le opzioni lascerebbe il menu
  // che mostra «qualunque» mentre lo stato tiene ancora la scelta vecchia — e
  // il job partirebbe fissato su una sonda che l'elenco non offre più.
  useEffect(() => {
    setRunOn((current) => {
      if (current.startsWith('probe:')) {
        return sondeIdonee.some((p) => `probe:${p.id}` === current) ? current : ''
      }
      if (current.startsWith('group:')) {
        return gruppiIdonei.some((g) => `group:${g.id}` === current) ? current : ''
      }
      return current
    })
    // Dipende dalle liste filtrate, che cambiano col tipo e all'arrivo dei dati.
  }, [sondeIdonee, gruppiIdonei])

  // Un nome proposto, non imposto: pianificare significa ritrovarla in elenco
  // fra sei mesi, e «snmp_sweep» non basterà a riconoscerla.
  useEffect(() => {
    if (mode === 'schedule' && !name) setName(t('{tipo} — ricorrente', { tipo: t(kind.label) }))
  }, [mode, kind.label, name])

  /**
   * Le opzioni si mostrano **una per una**, come le dichiara il catalogo.
   *
   * Prima bastava che l'elenco non fosse vuoto e si rendevano tutte insieme
   * quelle di SNMP: la discovery di rete, che dichiara solo `plan`, mostrava
   * profondità, ping, DNS inverso e topologia — opzioni che non le
   * appartengono — e **nessun modo di scegliere il piano**. Il tipo era in
   * elenco, cliccabile, e non c'era modo di usarlo.
   */
  const shows = (option: string) => kind.options.includes(option)
  // I tipi a endpoint (host[:porta]) invece che ad ambito di rete: per
  // l'inventario database la porta è per istanza, non un ambito da scandire.
  const aEndpoint = kind.kind === 'db_inventory'
  const snmpOptions = [
    'deep', 'ping_first', 'reverse_dns', 'record_unmanaged', 'topology', 'mac_table',
    'entity_inventory', 'routing_table',
  ]
  const hasSnmpOptions = snmpOptions.some(shows)

  // Un tipo che non dichiara tipi di credenziale non ne vuole a livello di job:
  // le sue credenziali stanno altrove. È il caso della discovery a piani, dove
  // ogni passo ha le proprie — SNMP per gli apparati, SSH per i server Unix,
  // Windows per gli altri — e un elenco unico non saprebbe quale dare a chi.
  const wantsCredential = kind.credential_kinds.length > 0

  // In modalità pool il campo obbligatorio non è un `required` del browser —
  // una casella non spuntata non lo fa scattare — quindi il modulo si blocca
  // qui. Senza, si arriverebbe al rifiuto del Control Plane per scoprire una
  // cosa che si vede guardando la schermata.
  const senzaAmbito =
    kind.needs_targets &&
    !shows('plan') &&
    scopeMode === 'pool' &&
    poolIds.length === 0

  async function submit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setBusy(true)
    try {
      // Solo la modalità scelta viene mandata. Mandare anche l'altra
      // rimetterebbe la somma silenziosa che questo modulo ha appena tolto: un
      // ambito rimasto scritto in un campo non più visibile finirebbe nella
      // scansione senza che nessuno l'abbia confermato.
      const scope =
        kind.needs_targets && scopeMode === 'manual'
          ? targets.split(',').map((t) => t.trim()).filter(Boolean)
          : []
      const common = {
        kind: kind.kind,
        targets: scope,
        // Un solo menu nella console, due campi nell'API: «group:<id>» è un
        // gruppo ordinato, tutto il resto una credenziale singola. Sono
        // alternative, e comporle richiederebbe una regola su quale viene prima
        // — una decisione che nessuno prenderebbe consapevolmente da un form.
        credential_id: credentialId.startsWith('group:') ? null : credentialId || null,
        credential_group_id: credentialId.startsWith('group:')
          ? credentialId.slice('group:'.length)
          : null,
        // Il piano da eseguire. Il Control Plane ne **copia** i passi nella spec
        // del job: il piano è un modello, il job è il verbale di ciò che è stato
        // fatto.
        plan_id: shows('plan') ? planId || null : null,
        // In un job i pool si copiano, in una pianificazione si referenziano:
        // la differenza la fa il Control Plane, qui si manda la stessa cosa.
        pool_ids: kind.needs_targets && scopeMode === 'pool' ? poolIds : [],
        mac_table: macTable,
        port: port ? Number(port) : null,
        // Vuoto = la sonda decide sui propri core. Mandare un numero comunque
        // riporterebbe il valore fisso che il dimensionamento serve a togliere.
        concurrency: concurrency ? Number(concurrency) : null,
        deep,
        // Il ritmo non è un campo del modulo, ma se viene da una scansione va
        // **portato**: rimandare il default mentre tutto il resto arriva dal job
        // creerebbe una pianificazione che gira a una velocità diversa da quella
        // provata — su un apparato fragile è la differenza fra un inventario e
        // un apparato che smette di rispondere.
        ...(initial ? { rate_limit_pps: initial.rate_limit_pps } : {}),
        ping_first: pingFirst,
        reverse_dns: reverseDns,
        record_unmanaged: recordUnmanaged,
        entity_inventory: entityInventory,
        routing_table: routingTable,
        ...(shows('sample_root') && sampleRoot.trim() ? { sample_root: sampleRoot.trim() } : {}),
        topology,
        skip_covered: shows('skip_covered') ? skipCovered : false,
        // Un solo campo nella console, due nell'API: «probe:<id>» vincola la
        // sonda, «group:<id>» il gruppo. Sono alternative — sceglierne una
        // esclude l'altra — e presentarle come due menu farebbe credere che si
        // possano combinare, con una semantica che nessuno saprebbe indovinare.
        probe_id: runOn.startsWith('probe:') ? runOn.slice(6) : null,
        group_id: runOn.startsWith('group:') ? runOn.slice(6) : null,
      }

      // Il gemello di backup: stessi bersagli, stessa sonda, credenziale SSH
      // sua. Due job e non un job doppio, perché sono due lavori con due
      // rapporti — e il backup può fallire senza sporcare la scansione.
      const backup =
        backupConfig && backupCredentialId
          ? {
              ...common,
              kind: 'config_backup',
              credential_id: backupCredentialId,
              credential_group_id: null,
              plan_id: null,
            }
          : null

      if (mode === 'run') {
        const job = await api.createJob({
          ...common,
          title: titolo.trim() || null,
          description: descrizione.trim() || null,
        })
        if (backup)
          await api.createJob({
            ...backup,
            title: titolo.trim() ? t('{nome} — backup configurazioni', { nome: titolo.trim() }) : null,
            description: descrizione.trim() || null,
          })
        onDone(
          backup
            ? t('{tipo}: job {id}… avviato, con il backup configurazioni a seguire.', {
                tipo: t(kind.label),
                id: job.id.slice(0, 8),
              })
            : t('{tipo}: job {id}… avviato. Verrà assegnato a una sonda online.', {
                tipo: t(kind.label),
                id: job.id.slice(0, 8),
              }),
        )
      } else {
        await api.createSchedule({ ...common, name: name.trim(), cron_expression: cron, timezone })
        if (backup) {
          await api.createSchedule({
            ...backup,
            name: t('{nome} — backup configurazioni', { nome: name.trim() }),
            cron_expression: cron,
            timezone,
          })
        }
        onDone(
          backup
            ? t('Pianificazioni «{nome}» e «{backup}» create.', {
                nome: name.trim(),
                backup: t('{nome} — backup configurazioni', { nome: name.trim() }),
              })
            : t('Pianificazione «{nome}» creata.', { nome: name.trim() }),
        )
      }
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={submit}>
      <ErrorBanner error={error} />

      {!kind.licensed && (
        <div className="banner banner--warn">
          {t('Non compreso nella licenza')}
        </div>
      )}
      {kind.licensed && !kind.probe_available && (
        <div className="banner banner--error">
          {t('Nessuna sonda online ha la capability {capability} abilitata: il job resterebbe in attesa. Verifica la scheda della sonda.', {
            capability: kind.capability,
          })}
        </div>
      )}

      {mode === 'run' && (
        <div className="row">
          <div className="field">
            <label htmlFor="job-title">{t('Titolo')}</label>
            <input
              id="job-title"
              type="text"
              value={titolo}
              onChange={(e) => setTitolo(e.target.value)}
              maxLength={160}
              placeholder={t('facoltativo: per riconoscere il job nell’elenco')}
            />
          </div>
          <div className="field">
            <label htmlFor="job-description">{t('Descrizione')}</label>
            <input
              id="job-description"
              type="text"
              value={descrizione}
              onChange={(e) => setDescrizione(e.target.value)}
              maxLength={4000}
              placeholder={t('facoltativo: perché, per chi, cosa aspettarsi')}
            />
          </div>
        </div>
      )}

      {mode === 'schedule' && (
        <div className="row">
          <div className="field">
            <label htmlFor="sched-name">{t('Nome della pianificazione')}</label>
            <input
              id="sched-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div className="field">
            <label htmlFor="sched-cron">{t('Quando')}</label>
            <select id="sched-cron" value={cron} onChange={(e) => setCron(e.target.value)}>
              {CRON_PRESETS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label()}
                </option>
              ))}
              {!CRON_PRESETS.some((p) => p.value === cron) && <option value={cron}>{cron}</option>}
            </select>
          </div>
          <div className="field">
            <label htmlFor="sched-tz">{t('Fuso orario')}</label>
            <input
              id="sched-tz"
              type="text"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
            />
            <p className="field__hint">
              {t('«Ogni notte alle 2:00» significa le 2:00 qui, non le 2:00 UTC.')}
            </p>
          </div>
        </div>
      )}

      {shows('plan') && (
        <div className="row">
          <div className="field" style={{ flex: '2 1 320px' }}>
            <label htmlFor="df-plan">{t('Piano da eseguire')}</label>
            <select
              id="df-plan"
              value={planId}
              onChange={(e) => setPlanId(e.target.value)}
              required
            >
              <option value="">
                {(plans.data ?? []).some((p) => p.is_default)
                  ? t('Piano predefinito')
                  : t('Seleziona…')}
              </option>
              {(plans.data ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.is_default
                    ? t('{nome} — predefinito ({n} passi)', { nome: p.name, n: (p.steps ?? []).length })
                    : t('{nome} ({n} passi)', { nome: p.name, n: (p.steps ?? []).length })}
                </option>
              ))}
            </select>
            {(plans.data ?? []).length === 0 ? (
              <p className="field__hint">
                {conLink(
                  t('Nessun piano. Una discovery di rete è la sequenza di passi che qualcuno ha disegnato — ping, DNS, porte, SNMP, SSH, Windows — e senza non c’è niente da eseguire. {link}.'),
                  <Link to="/discovery-plans/nuovo">{t('Creane uno')}</Link>,
                )}
              </p>
            ) : (
              <p className="field__hint">
                {/* Le credenziali stanno sui passi, e chi arriva qui non ha
                    davanti la schermata in cui si scelgono: dirlo qui evita di
                    cercarle in questo modulo, dove non ci sono. */}
                {conLink(
                  t('I passi vengono copiati nel job: il piano è il modello, il job è il verbale di ciò che è stato fatto. Le credenziali stanno sui singoli passi del piano. {link}.'),
                  <Link to="/discovery-plans">{t('Rivedi i piani')}</Link>,
                )}
              </p>
            )}
          </div>
        </div>
      )}

      <div className="row">
        {/* L'ambito: **una scelta**, non due campi che si sommano.

            Prima c'erano l'uno accanto all'altro — un campo di testo e un
            elenco di pool — e la domanda «cosa succede se li riempio
            entrambi?» non aveva una risposta visibile. Due campi che si
            sommano in silenzio sono la stessa ambiguità delle credenziali
            singola-o-gruppo, risolta lì nello stesso modo: si sceglie di quale
            si sta parlando, e si vede solo quello. */}
        {kind.needs_targets && (
          <div className="field" style={{ flex: '2 1 320px' }}>
            <label>{t('Ambito da scansionare')}</label>
            <div className="rowactions" style={{ textAlign: 'left', margin: '0 0 10px' }}>
              <button
                type="button"
                className={scopeMode === 'manual' ? '' : 'secondary'}
                onClick={() => setScopeMode('manual')}
              >
                {t('Scrivo io reti e indirizzi')}
              </button>
              <button
                type="button"
                className={scopeMode === 'pool' ? '' : 'secondary'}
                onClick={() => setScopeMode('pool')}
              >
                {t('Uso un pool')}
              </button>
            </div>

            {scopeMode === 'manual' ? (
              <>
                <input
                  id="df-targets"
                  type="text"
                  value={targets}
                  onChange={(e) => setTargets(e.target.value)}
                  placeholder={aEndpoint
                    ? 'db1.local, db1.local:5433, 10.0.0.5:1521'
                    : '192.168.1.0/24, 10.0.0.5, 10.0.1.10-10.0.1.50'}
                  required={!shows('plan')}
                />
                <p className="field__hint">
                  {aEndpoint ? (
                    <>
                      {t('Un host (o IP) per istanza, separati da virgola. Aggiungi «:porta» per più istanze sullo stesso host — senza porta si usa quella standard del motore.')}
                    </>
                  ) : (
                    <>
                      {t('Subnet CIDR (max /16), indirizzi singoli o intervalli, separati da virgola.')}
                      {shows('plan') && ' ' + t('Vuoto: si usano gli ambiti predefiniti del piano.')}
                    </>
                  )}
                </p>
              </>
            ) : (pools.data ?? []).length === 0 ? (
              <p className="field__hint">
                {conLink(
                  t('Nessun pool ancora. {link}: le reti si scrivono una volta e le richiamano tutte le scansioni.'),
                  <Link to="/pools">{t('Creane uno')}</Link>,
                )}
              </p>
            ) : (
              <>
                {/* Caselle e non un elenco a selezione multipla: quello si usa
                    tenendo premuto un tasto che nessuno indovina, e con tre
                    pool in tutto occupa lo stesso spazio dicendo di meno. */}
                {(pools.data ?? []).map((p) => (
                  <div className="checkbox" key={p.id}>
                    <input
                      id={`df-pool-${p.id}`}
                      type="checkbox"
                      checked={poolIds.includes(p.id)}
                      onChange={(e) =>
                        setPoolIds(
                          e.target.checked
                            ? [...poolIds, p.id]
                            : poolIds.filter((id) => id !== p.id),
                        )
                      }
                    />
                    <label htmlFor={`df-pool-${p.id}`}>
                      {p.name}
                      {/* Cosa c'è dentro, senza andarlo a cercare: un pool è un
                          nome, e scegliere un nome senza sapere quante reti
                          contenga è scegliere alla cieca.

                          Per un dinamico il numero è quello di **adesso**, e va
                          detto: la scansione lo ricalcola quando parte, quindi
                          domani può essere un altro. */}
                      <small>
                        {p.kind === 'dynamic'
                          ? t('{n} apparati adesso — dalle condizioni', {
                              n: p.entries_count.toLocaleString(localeCorrente()),
                            })
                          : t('{n} ambiti · {indirizzi} indirizzi', {
                              n: p.entries_count,
                              indirizzi: p.addresses.toLocaleString(localeCorrente()),
                            })}
                        {p.description ? ` · ${p.description}` : ''}
                      </small>
                    </label>
                  </div>
                ))}

                {poolIds.length > 0 && (
                  <p className="field__hint">
                    {t('In tutto {n} indirizzi.', { n: indirizziDeiPool.toLocaleString(localeCorrente()) })}{' '}
                    {mode === 'schedule'
                      ? t('Per riferimento: una subnet aggiunta al pool entra nella scansione della notte successiva.')
                      : t('Copiati nel job: resta il verbale di ciò che è stato guardato oggi.')}
                  </p>
                )}

                {/* Oltre il tetto la sonda rifiuta il job **intero**, non ne
                    guarda una parte: dirlo qui è la differenza fra togliere un
                    pool adesso e scoprire domani che l'inventario non c'è. */}
                {tettoParte != null && parti > 1 && (
                  <p className="field__hint">
                    {t('La sonda ne guarda al massimo {tetto} per job: la scansione si divide da sé in {k} job, uno per parte, che le sonde eseguono come gli altri.', {
                      tetto: tettoParte.toLocaleString(localeCorrente()),
                      k: parti,
                    })}
                  </p>
                )}
                {oltreIlTetto && (
                  <p className="banner banner--warn" style={{ marginTop: 8 }}>
                    {t('Sono {n} indirizzi e una scansione ne espande al massimo {tetto}: la sonda rifiuterebbe il job intero. Scegli meno pool, o dividili.', {
                      n: indirizziDeiPool.toLocaleString(localeCorrente()),
                      tetto: tettoIndirizzi.toLocaleString(localeCorrente()),
                    })}
                  </p>
                )}

                {/* Ciò che è rimasto scritto nell'altro campo, che adesso non
                    si vede. Vale sia per chi ha digitato e poi ha cambiato
                    idea, sia per una scansione ricompilata che aveva entrambi:
                    in tutti e due i casi quegli ambiti non partono, e senza
                    questa riga sparirebbero senza che nessuno lo sappia. */}
                {targets.trim() !== '' && (
                  <p className="field__hint">
                    {t('Restano scritti a mano {ambiti}, e con il pool non vengono inclusi. Torna su «Scrivo io» per usarli.', {
                      ambiti: targets.trim(),
                    })}
                  </p>
                )}
              </>
            )}
          </div>
        )}

        {/* I pool accanto all'ambito scritto a mano, non al suo posto: i due si
            sommano, ed è il caso vero — il perimetro di sempre più la subnet
            nuova che si sta provando. */}
        {kind.needs_targets && (
          <div className="field" style={{ flex: '1 1 240px' }}>
            <label htmlFor="df-pools">{t('Pool di asset')}</label>
            <select
              id="df-pools"
              multiple
              size={Math.min(Math.max((pools.data ?? []).length, 2), 5)}
              value={poolIds}
              onChange={(e) =>
                setPoolIds(Array.from(e.target.selectedOptions, (o) => o.value))
              }
            >
              {(pools.data ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ·{' '}
                  {t('{n} ambiti · {indirizzi} indirizzi', { n: p.entries_count, indirizzi: p.addresses })}
                </option>
              ))}
            </select>
            <p className="field__hint">
              {(pools.data ?? []).length === 0 ? (
                <>
                  {conLink(
                    t('Nessun pool. {link} per scrivere gli ambiti una volta sola.'),
                    <Link to="/pools">{t('Creane uno')}</Link>,
                  )}
                </>
              ) : mode === 'schedule' ? (
                // La differenza si dice qui, dove si decide: è l'unica ragione
                // per cui vale la pena usare un pool invece di incollare le
                // subnet, e non si vede da nessuna altra parte.
                t('Per riferimento: una subnet aggiunta al pool entra nella scansione della notte successiva.')
              ) : (
                t('Gli ambiti vengono copiati nel job: resta il verbale di ciò che è stato guardato oggi.')
              )}
            </p>
          </div>
        )}

        {/* Su quale sonda. Un solo menu e non due: sonda e gruppo sono
            alternative, e presentarle separate farebbe credere che si possano
            combinare — con una semantica che nessuno saprebbe indovinare. */}
        <div className="field">
          <label htmlFor="df-runon">{t('Esegui su')}</label>
          <select id="df-runon" value={runOn} onChange={(e) => setRunOn(e.target.value)}>
            <option value="">{t('Qualunque sonda idonea')}</option>
            {gruppiIdonei.length > 0 && (
              <optgroup label={t('Gruppi')}>
                {gruppiIdonei.map((g) => (
                  <option key={g.id} value={`group:${g.id}`}>
                    {g.members === 1
                      ? t('{nome} (1 sonda)', { nome: g.name })
                      : t('{nome} ({n} sonde)', { nome: g.name, n: g.members })}
                  </option>
                ))}
              </optgroup>
            )}
            {sondeIdonee.length > 0 && (
              <optgroup label={t('Sonde')}>
                {sondeIdonee.map((p) => (
                  <option key={p.id} value={`probe:${p.id}`}>
                    {p.status === 'online' ? p.name : t('{nome} — non in linea', { nome: p.name })}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
          <p className="field__hint">
            {runOn.startsWith('probe:') ? (
              <>
                {t('La discovery verrà eseguita solo da questa sonda. Se non è disponibile resta in attesa e non passa a un’altra: scegliere la sonda è scegliere la rete da cui guardare, e un’altra rete darebbe un inventario vuoto che sembra completo.')}
              </>
            ) : runOn.startsWith('group:') ? (
              <>{t('Una sonda del gruppo, scelta con la politica di assegnazione del gruppo.')}</>
            ) : (
              <>
                {t('La sceglie il Control Plane fra quelle online che dichiarano la capability necessaria. Va bene quando le sonde vedono la stessa rete.')}
              </>
            )}
          </p>
        </div>

        {!wantsCredential ? (
          <div className="field">
            <p className="field__hint">
              {/* Non un campo disabilitato: un campo che non si può compilare fa
                  cercare cosa manca. Qui non manca niente — la credenziale è
                  altrove, e va detto dov'è. */}
              {t('Nessuna credenziale da scegliere qui: questo tipo di discovery usa quelle dichiarate sui singoli passi del piano, perché ne servono di tipi diversi — SNMP per gli apparati, SSH per i server Unix, Windows per gli altri — e un elenco unico non saprebbe quale dare a chi.')}
            </p>
          </div>
        ) : (
        <div className="field">
          <label htmlFor="df-cred">{t('Credenziale')}</label>
          <select
            id="df-cred"
            value={credentialId}
            onChange={(e) => setCredentialId(e.target.value)}
            required
          >
            <option value="">{t('Seleziona…')}</option>
            {/* I gruppi per primi: quando ne esiste uno è quasi sempre la scelta
                giusta — è stato creato apposta perché una credenziale sola non
                bastava. */}
            {usableGroups.length > 0 && (
              <optgroup label={t('Gruppi (provati in ordine)')}>
                {usableGroups.map((g) => (
                  <option key={g.id} value={`group:${g.id}`}>
                    {t('{nome} — {n} credenziali', { nome: g.name, n: g.members.length })}
                  </option>
                ))}
              </optgroup>
            )}
            <optgroup label={t('Credenziale singola')}>
              {usable.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} ({c.kind})
                </option>
              ))}
            </optgroup>
          </select>
          {credentialId.startsWith('group:') && (
            <p className="field__hint">
              {/* L'ordine è la funzione, e va ricordato dove si sceglie: chi
                  seleziona un gruppo qui non ha davanti la schermata in cui la
                  sequenza si decide. */}
              {t('La sonda le prova in ordine e si ferma alla prima che risponde. La sequenza conta: ogni tentativo fallito costa un timeout per ogni apparato, e con SNMPv3 un’autenticazione fallita resta tale — alcuni apparati bloccano l’utenza dopo qualche tentativo.')}
            </p>
          )}
          {usable.length === 0 && (
            <p className="field__hint">
              {t('Nessuna credenziale di tipo {tipi}: creala nella sezione Credenziali.', {
                tipi: kind.credential_kinds.join(', '),
              })}
            </p>
          )}
          </div>
        )}

        {shows('port') && (
          <>
            <div className="field" style={{ flex: '0 1 120px' }}>
              <label htmlFor="df-port">{t('Porta')}</label>
              <input
                id="df-port"
                type="number"
                value={port}
                onChange={(e) => setPort(e.target.value)}
                placeholder={kind.kind.startsWith('snmp') ? '161' : '22'}
                min={1}
                max={65535}
              />
              {/* Backup e inventario SSH: la porta vale per gli apparati che
                  non ne portano una nell'indirizzo; chi ce l'ha già la tiene. */}
              {!kind.kind.startsWith('snmp') && (
                <small className="field__hint">{t('Vuota: 22. Vale per gli apparati senza porta nell’indirizzo.')}</small>
              )}
            </div>
          </>
        )}

        {shows('sample_root') && mode === 'run' && (
          <div className="field" style={{ flex: '1 1 360px' }}>
            <label htmlFor="df-sample-root">{t('Sottoalberi da campionare')}</label>
            <input
              id="df-sample-root"
              type="text"
              className="mono"
              value={sampleRoot}
              onChange={(e) => setSampleRoot(e.target.value)}
              placeholder={t('automatico')}
            />
            <p className="field__hint">
              {t('Vuoto: la sonda sceglie — la MIB del prodotto quando la conosce (su un Fabric Interconnect Cisco UCS telaio, blade, CPU e memoria), altrimenti il ramo del costruttore. Più OID separati da virgola.')}
            </p>
          </div>
        )}

        {shows('concurrency') && (
          <div className="field" style={{ flex: '0 1 190px' }}>
            <label htmlFor="df-conc">{t('Interrogazioni parallele')}</label>
            <input
              id="df-conc"
              type="number"
              value={concurrency}
              onChange={(e) => setConcurrency(e.target.value)}
              placeholder={t('automatico')}
              min={1}
              max={512}
            />
            <p className="field__hint">{t('Vuoto: la sonda si dimensiona sui propri core.')}</p>
          </div>
        )}
      </div>

      {!kind.needs_targets && (
        <p className="field__hint" style={{ marginBottom: 12 }}>
          {t("L'inventario copre l'intero account o piattaforma: l'endpoint e l'ambito provengono dalla credenziale.")}
        </p>
      )}

      {hasSnmpOptions && (
        <fieldset style={{ border: 'none', padding: 0, margin: '8px 0 16px' }}>
          <legend className="field__hint" style={{ marginBottom: 8 }}>
            {t('OPZIONI')}
          </legend>

          {shows('deep') && (
            <div className="checkbox">
              <input
                id="df-deep"
                type="checkbox"
                checked={deep}
                onChange={(e) => setDeep(e.target.checked)}
              />
              <label htmlFor="df-deep">
                {t('Discovery approfondita')}
                <small>
                  {t('Raccoglie interfacce, seriali e numeri di modello oltre ai dati di base.')}
                </small>
              </label>
            </div>
          )}

          {shows('ping_first') && (
            <div className="checkbox">
              <input
                id="df-ping"
                type="checkbox"
                checked={pingFirst || recordUnmanaged}
                disabled={recordUnmanaged}
                onChange={(e) => setPingFirst(e.target.checked)}
              />
              <label htmlFor="df-ping">
                {t('Verifica raggiungibilità prima di interrogare')}
                <small>
                  {t('Molto più rapida su ambiti ampi: interroga solo gli host che rispondono.')}
                </small>
              </label>
            </div>
          )}

          {shows('reverse_dns') && (
            <div className="checkbox">
              <input
                id="df-dns"
                type="checkbox"
                checked={reverseDns}
                onChange={(e) => setReverseDns(e.target.checked)}
              />
              <label htmlFor="df-dns">
                {t('Risoluzione DNS inversa')}
                <small>{t('Aggiunge il nome DNS ai CI trovati.')}</small>
              </label>
            </div>
          )}

          {shows('topology') && (
            <div className="checkbox">
              <input
                id="df-topo"
                type="checkbox"
                checked={topology}
                onChange={(e) => setTopology(e.target.checked)}
              />
              <label htmlFor="df-topo">
                {t('Rileva i collegamenti tra apparati')}
                <small>
                  {t('Raccoglie i vicinati CDP e LLDP per la mappa delle adiacenze. Richiede la discovery approfondita.')}
                </small>
              </label>
            </div>
          )}



          {['network', 'snmp_sweep'].includes(kind.kind) && (
            <div className="checkbox">
              <input
                id="df-backupconfig"
                type="checkbox"
                checked={backupConfig}
                onChange={(e) => setBackupConfig(e.target.checked)}
              />
              <label htmlFor="df-backupconfig">
                {t('Salva anche le configurazioni degli apparati (backup)')}
                <small>
                  {t('Alla conferma parte un secondo job di backup sugli stessi bersagli, con la credenziale SSH scelta qui sotto: due job e due rapporti, così il backup può fallire senza sporcare la scansione.')}
                </small>
              </label>
            </div>
          )}
          {backupConfig && ['network', 'snmp_sweep'].includes(kind.kind) && (
            <div className="field" style={{ maxWidth: 360, marginLeft: 28 }}>
              <label htmlFor="df-backupcred">{t('Credenziale SSH per il backup')}</label>
              <select
                id="df-backupcred"
                value={backupCredentialId}
                onChange={(e) => setBackupCredentialId(e.target.value)}
                required
              >
                <option value="">—</option>
                {credentials
                  .filter((c) => c.kind === 'ssh_key' || c.kind === 'ssh_password')
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
              </select>
            </div>
          )}

          {shows('mac_table') && (
            <div className="checkbox">
              <input
                id="df-mactable"
                type="checkbox"
                checked={macTable}
                onChange={(e) => setMacTable(e.target.checked)}
              />
              <label htmlFor="df-mactable">
                {t('Rileva cosa è attaccato a ogni porta')}
                <small>
                  {/* La differenza con l'opzione qui sopra è il punto: CDP e
                      LLDP li parlano gli apparati di rete e poco altro, mentre
                      una stampante o un PC lasciano solo il proprio indirizzo
                      fisico nella tabella dello switch. */}
                  {t('Legge gli indirizzi fisici imparati dalle porte dello switch e li correla con i CI già in inventario. Vede anche ciò che non parla CDP/LLDP — stampanti, PC, macchine virtuali. Le porte con troppi indirizzi sono dorsali e vengono escluse: «raggiungibile attraverso» non è «attaccato a».')}
                </small>
              </label>
            </div>
          )}

          {shows('record_unmanaged') && (
            <div className="checkbox">
              <input
                id="df-unmanaged"
                type="checkbox"
                checked={recordUnmanaged}
                onChange={(e) => setRecordUnmanaged(e.target.checked)}
              />
              <label htmlFor="df-unmanaged">
                {t('Registra anche i dispositivi senza SNMP')}
                <small>
                  {t('Salva in CMDB gli host raggiungibili ma non inventariabili. Richiede la verifica di raggiungibilità.')}
                </small>
              </label>
            </div>
          )}
          {shows('entity_inventory') && (
            <div className="checkbox">
              <input
                id="df-entity"
                type="checkbox"
                checked={entityInventory}
                onChange={(e) => setEntityInventory(e.target.checked)}
              />
              <label htmlFor="df-entity">
                {t('Inventario componenti (SFP, schede, alimentatori)')}
                <small>
                  {t('Legge dalla ENTITY-MIB i componenti fisici con il loro seriale. Richiede la discovery approfondita. Le SFP di terze parti spesso non hanno seriale in SNMP: per quelle è più completo l’inventario avanzato via SSH.')}
                </small>
              </label>
            </div>
          )}
          {shows('routing_table') && (
            <div className="checkbox">
              <input
                id="df-routing"
                type="checkbox"
                checked={routingTable}
                onChange={(e) => setRoutingTable(e.target.checked)}
              />
              <label htmlFor="df-routing">
                {t('Tabelle di routing (L3)')}
                <small>
                  {t('Raccoglie la tabella di routing dell’apparato via SNMP (inetCidrRoute/ipCidrRoute/ipRoute), mostrata nella sezione «Routing» del CI. Richiede la discovery approfondita. Su un router con molte rotte è voluminosa: è troncata a un tetto.')}
                </small>
              </label>
            </div>
          )}
          {shows('skip_covered') && (
            <div className="checkbox">
              <input
                id="df-skip-covered"
                type="checkbox"
                checked={skipCovered}
                onChange={(e) => setSkipCovered(e.target.checked)}
              />
              <label htmlFor="df-skip-covered">
                {t('Salta gli indirizzi già coperti da altre discovery')}
                <small>
                  {t('Gli indirizzi già in CMDB o già bersaglio di un altro job vengono verificati vivi ma non ri-scansionati a fondo, e il rapporto dice da dove sono coperti. Utile su una subnet larga che si sovrappone ad altri job.')}
                </small>
              </label>
            </div>
          )}
        </fieldset>
      )}

      <div className="toolbar">
        <button type="submit" disabled={busy || !kind.licensed || senzaAmbito}>
          {busy
            ? mode === 'run'
              ? t('Avvio…')
              : t('Salvataggio…')
            : mode === 'run'
              ? t('Avvia ora')
              : t('Crea pianificazione')}
        </button>
        <button type="button" className="secondary" onClick={onCancel}>
          {t('Annulla')}
        </button>
      </div>
    </form>
  )
}
